import { I18n } from '@affine/i18n';
import { LiveData, Service } from '@toeverything/infra';
import { map } from 'rxjs';

import {
  isBackendAvailableForImmediateRequest,
  subscribeBackendAvailabilityUi,
  subscribeBackendRecovery,
} from '../../../utils/backend-availability';
import { showLocalSystemNotification } from '../../../utils/local-system-notification';
import type { WorkspaceServerService } from '../../cloud';
import type { GlobalState } from '../../storage';
import type { WorkspaceService } from '../../workspace';
import { formatOfflineAccessRemaining } from '../offline-access-format';
import type { GuardStore } from '../stores/guard';
import type { PermissionSnapshotStore } from '../stores/permission-snapshot';
import {
  OfflineAccessScheduler,
  type OfflineAccessWarning,
} from './offline-access-scheduler';

/**
 * Workspace-scoped offline-access lifecycle: proactive renewal of signed
 * grants while online, expiry warning, one local system notification per
 * expiry window and the "expired" state for locked docs.
 */
export class OfflineAccessService extends Service {
  private readonly scheduler: OfflineAccessScheduler | null;

  readonly warning$: LiveData<OfflineAccessWarning | null>;

  /** True while offline permissions could not be stored completely. */
  readonly vaultFull$: LiveData<boolean>;

  constructor(
    private readonly permissionSnapshotStore: PermissionSnapshotStore,
    private readonly guardStore: GuardStore,
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly globalState: GlobalState
  ) {
    super();
    const workspace = this.workspaceService.workspace;
    const cloudMemberWorkspace =
      workspace.flavour !== 'local' &&
      !workspace.openOptions.isSharedMode &&
      !workspace.openOptions.externalDocAccess;
    if (!cloudMemberWorkspace) {
      this.scheduler = null;
      this.warning$ = new LiveData<OfflineAccessWarning | null>(null);
      this.vaultFull$ = new LiveData(false);
      return;
    }

    const scheduler = new OfflineAccessScheduler({
      store: this.permissionSnapshotStore,
      renewPartition: partitionIndex =>
        this.guardStore.refreshPartition(partitionIndex),
      isOnline: () =>
        !!this.workspaceServerService.server &&
        isBackendAvailableForImmediateRequest(),
      notify: warning => this.notifySystem(warning),
      readLastNotifiedExpiresAt: () =>
        this.globalState.get<number>(this.lastNotifiedKey) ?? null,
      writeLastNotifiedExpiresAt: expiresAt =>
        this.globalState.set(this.lastNotifiedKey, expiresAt),
    });
    this.scheduler = scheduler;
    this.warning$ = scheduler.warning$;
    this.vaultFull$ = this.permissionSnapshotStore.vaultStatus$.map(
      status => status === 'degraded'
    );

    const grantsSubscription =
      this.permissionSnapshotStore.grantsVersion$.subscribe(() =>
        scheduler.evaluate()
      );
    const unsubscribeAvailability = subscribeBackendAvailabilityUi(() =>
      scheduler.evaluate()
    );
    const unsubscribeRecovery = subscribeBackendRecovery(() =>
      scheduler.renewNow()
    );
    this.disposables.push(
      () => grantsSubscription.unsubscribe(),
      unsubscribeAvailability,
      unsubscribeRecovery,
      () => scheduler.dispose()
    );
  }

  /** True once the offline grant for this doc has run out without renewal. */
  docAccessExpired$(docId: string): LiveData<boolean> {
    return LiveData.from(
      this.permissionSnapshotStore.expiredGrants$.pipe(
        map(() => this.permissionSnapshotStore.isGrantExpired('doc', docId))
      ),
      this.permissionSnapshotStore.isGrantExpired('doc', docId)
    );
  }

  /** Reconnect hook for callers that detect connectivity themselves. */
  renewNow(): void {
    this.scheduler?.renewNow();
  }

  private get lastNotifiedKey(): string {
    return `offline-access:last-notified-expires-at:${this.workspaceService.workspace.id}`;
  }

  private notifySystem(warning: OfflineAccessWarning): void {
    showLocalSystemNotification({
      title: I18n['com.streamlain.offline-access.warning.title'](),
      body: I18n['com.streamlain.offline-access.warning.system-message']({
        remaining: formatOfflineAccessRemaining(warning.remainingMs),
      }),
      tag: this.lastNotifiedKey,
    });
  }
}
